import { ReactNode, useId } from 'react'

/**
 * SOCLE COMMUN DES CONTRÔLES DE SAISIE (LOT-H6).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La recette DA du 2026-08-09 a compté, sur 24 écrans : **des `<select>` natifs
 * non stylés sur 11 écrans** (DA-023), **deux modèles de sélecteur à 20 px
 * d'écart dans la même barre d'outils** (DA-022), **5 sélecteurs à bordure
 * violette** — l'accent interdit (DA-024), **2 `<input type="date">` natifs**
 * dans une modale par ailleurs dessinée (DA-025), une **case à cocher native**
 * de ~16 px collée au bouton principal (DA-008) et un **anneau de focus TEAL**
 * (DA-026), c'est-à-dire un cinquième accent pour dire « où je suis au clavier ».
 *
 * Comme pour le bouton (H3), la cause n'est pas le goût : c'est l'absence de
 * source unique. Ce fichier porte ce que les cinq contrôles ont en commun —
 * le gabarit du champ (libellé / aide / erreur) et les classes de chrome.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LES DEUX MÉTRIQUES, ET L'ANNEAU
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `h-control` (42 px) est la hauteur DESSINÉE ; `min-h-touch` (44 px) la cible
 * TACTILE iPad (design system §3). Les contrôles de saisie reprennent EXACTEMENT
 * la recette du `Button` de H3 (`h-control min-h-touch`) : un sélecteur et un
 * bouton posés dans la même barre d'outils doivent s'aligner au pixel, sinon on
 * réintroduit par le bas le désordre que H3 a supprimé par le haut.
 *
 * L'anneau de focus est `primary`, **partout et sans exception** (DA-026). Un
 * anneau teal, violet ou cyan n'est pas une variante décorative : c'est la seule
 * information dont dispose un utilisateur au clavier pour savoir où il est.
 */

/**
 * Chrome commun à tout contrôle « boîte » — champ texte, sélecteur, champ date.
 *
 * `bg-background-secondary` (slate-800) sur `border-border-secondary`
 * (slate-700) : le champ est une surface SURÉLEVÉE par rapport à la carte
 * (slate-900), pas un trou. La bordure violette de DA-024 disparaît ici, pour
 * les cinq contrôles à la fois.
 */
export const CONTROL_BASE =
  'w-full rounded-control border border-border-secondary bg-background-secondary ' +
  'text-foreground transition-colors duration-150 ' +
  'placeholder:text-foreground-tertiary ' +
  'hover:border-neutral-600 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

/** Bordure et anneau quand le champ porte une erreur. Le rouge est un token. */
export const CONTROL_INVALID =
  'border-danger-500 hover:border-danger-500 focus:ring-danger-500 focus:border-danger-500'

export type ControlSize = 'md' | 'sm'

/**
 * GÉOMÉTRIE seule — la taille du texte est décidée par chaque contrôle, pour la
 * raison expliquée sous `CONTROL_TEXT`.
 *
 * `md` : 42 px dessinés, cible 44 px — le gabarit par défaut, celui qui
 * s'aligne avec `Button size="md"`.
 * `sm` : 32 px — la cote du `Button size="sm"` de H3, pas une troisième —
 * réservé aux barres de filtres denses. La cible tactile y est rendue par un
 * pseudo-élément (cf. `CONTROL_SM_HIT_AREA`), jamais en grossissant le
 * contrôle : le §3 du design system l'interdit explicitement.
 */
export const CONTROL_SIZES: Record<ControlSize, string> = {
  md: 'h-control min-h-touch px-3',
  sm: 'h-8 px-2.5',
}

/**
 * TAILLE DU TEXTE SAISI — 16 px, et c'est une contrainte d'APPAREIL, pas un
 * septième cran typographique.
 *
 * ⚠️ Safari iOS/iPadOS ZOOME la page à chaque focus sur un `<input>`, un
 * `<textarea>` ou un `<select>` dont la taille de texte est **inférieure à
 * 16 px**. La page reste zoomée après le focus : sur l'iPad, qui est la cible
 * du produit, une modale à 4 champs devient inutilisable. Le `<meta viewport>`
 * de `index.html` ne porte pas de `maximum-scale`, donc rien ne l'empêche.
 *
 * Avant ce lot, `.input` ne déclarait AUCUNE taille : les champs héritaient des
 * 16 px du document et ne zoomaient pas. Écrire `text-t4` (13.5 px) sur les
 * champs aurait donc introduit le défaut, pas corrigé quoi que ce soit.
 *
 * La valeur est arbitraire À DESSEIN : elle ne rejoint pas l'échelle du §2 —
 * qui régit le texte de PRODUIT, pas la valeur tapée dans un champ — et ne
 * doit surtout pas devenir un cran réutilisable ailleurs.
 *
 * Le déclencheur du `Select`, lui, est un `<button>` : il n'est pas concerné et
 * garde les crans `t4` / `t5`.
 */
export const CONTROL_TEXT = 'text-[1rem] leading-5'

/**
 * Cible tactile de la taille `sm`, reprise telle quelle du `Button` de H3 : un
 * pseudo-élément de 44 px centré verticalement, qui déborde du chrome sans
 * occuper de place dans le flux.
 */
export const CONTROL_SM_HIT_AREA =
  "before:absolute before:inset-x-0 before:top-1/2 before:h-touch before:-translate-y-1/2 before:content-['']"

export interface FieldProps {
  /** Libellé visible. Absent = le contrôle doit porter un `aria-label`. */
  label?: ReactNode
  /** Aide sous le champ. Masquée dès qu'une erreur est affichée. */
  hint?: ReactNode
  /** Message d'erreur. Rend le champ `aria-invalid`. */
  error?: ReactNode
  required?: boolean
  disabled?: boolean
  /** Classes du CONTENEUR (marge, largeur). Le contrôle a son propre `className`. */
  wrapperClassName?: string
}

interface FieldShellProps extends FieldProps {
  /** `id` du contrôle — cible du `htmlFor` et racine des `id` d'aide/erreur. */
  controlId: string
  children: ReactNode
}

/** `id` dérivés, partagés par le shell et le contrôle qu'il enveloppe. */
export function describedByIds(controlId: string, hint?: ReactNode, error?: ReactNode) {
  const ids: string[] = []
  if (error) ids.push(`${controlId}-error`)
  else if (hint) ids.push(`${controlId}-hint`)
  return ids.length > 0 ? ids.join(' ') : undefined
}

/**
 * Gabarit vertical d'un champ : libellé, contrôle, aide OU erreur.
 *
 * L'aide et l'erreur ne s'empilent jamais — quand les deux existent, l'erreur
 * gagne : deux lignes de service sous un champ déplacent tout ce qui suit, et
 * une modale de 4 champs se met à sauter à chaque frappe.
 */
export function FieldShell({
  controlId,
  label,
  hint,
  error,
  required,
  wrapperClassName = '',
  children,
}: FieldShellProps) {
  return (
    <div className={wrapperClassName}>
      {label && (
        <label htmlFor={controlId} className="mb-1.5 block text-t5 text-foreground-secondary">
          {label}
          {required && (
            <span className="text-danger-400" aria-hidden="true">
              {' *'}
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${controlId}-error`} className="mt-1.5 text-t5 text-danger-400">
          {error}
        </p>
      ) : hint ? (
        <p id={`${controlId}-hint`} className="mt-1.5 text-t5 text-foreground-tertiary">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/**
 * `id` stable : celui fourni par l'appelant, sinon un `useId` React.
 * Un champ SANS `id` ne peut pas porter de `<label for>` — donc pas d'étiquette
 * cliquable, donc une cible tactile amputée sur iPad.
 */
export function useControlId(providedId?: string) {
  const generated = useId()
  return providedId ?? generated
}
